import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useConfirmDraftTransferOrdersMutation,
  useImportTransferOrderMutation,
  useLazyGetTfOrdLineForCnsldPOQuery,
} from "../../../../store/apiSlice/orderApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

/**
 * TransferOrderAction Component
 *
 * Renders action buttons related to transfer orders:
 * - "Sync" button to import or synchronize transfer orders via an API call.
 * - "Confirm Draft" for Draft headers (leave-Draft gate → Created).
 * - "Create Consolidate Purchase Order" button (enabled based on validation).
 *
 * @param {Object} props - Component props
 * @param {Array<number|string>} props.selectedOrderId - Array of selected transfer order IDs
 * @param {Function} [props.setSelectedOrder] - Clears list selection after confirm
 * @return {JSX.Element}
 */
const TransferOrderAction = ({ selectedOrderId, setSelectedOrder }) => {
  const [ImportTransferOrder, { isLoading: importLoading }] = useImportTransferOrderMutation();
  const [confirmDraft, { isLoading: isConfirmingDraft }] = useConfirmDraftTransferOrdersMutation();

  const [getTfOrdLineForCnsldPO, { isLoading: ordLineForCnsldPOLoading }] =
    useLazyGetTfOrdLineForCnsldPOQuery();
  const { results } = useSelector(state => state.order.transferOrders);
  const navigate = useNavigate();
  const notify = useNotification();

  const selectedRows = useMemo(
    () => results.filter(obj => selectedOrderId.includes(obj.id)),
    [results, selectedOrderId],
  );

  const isDisabled = useMemo(() => {
    if (selectedOrderId.length < 1) return true;
    if (selectedRows.length < 1) return true;

    return selectedRows.some(
      obj =>
        !["Partially Consolidated", "Not Consolidated"].includes(obj.consolidation_status) ||
        obj.from_warehouse?.id !== selectedRows[0]?.from_warehouse?.id ||
        obj.status === "Draft",
    );
  }, [selectedRows, selectedOrderId]);

  const confirmDraftOk = useMemo(() => {
    if (!selectedRows.length) return false;
    return selectedRows.every(row => row.status === "Draft");
  }, [selectedRows]);

  const importTF = async () => {
    if (importLoading) return;

    try {
      const res = await ImportTransferOrder().unwrap();
      notify({ type: "success", message: res.detail });
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Something went wrong" });
    }
  };

  const handleConfirmDraft = async () => {
    if (!confirmDraftOk || isConfirmingDraft) return;
    try {
      const res = await confirmDraft({ ids: selectedOrderId }).unwrap();
      notify({ type: "success", message: res?.detail || "Draft confirmed." });
      setSelectedOrder?.([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  const handleCreateConsolidatedPOOrder = async () => {
    if (ordLineForCnsldPOLoading) return;

    try {
      await getTfOrdLineForCnsldPO({ tf_ord_ids: selectedOrderId }).unwrap();
      navigate("/orders/purchase/create/consolidate");
    } catch (error) {}
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "create-cnsld-po",
        label: "Create Consolidate Purchase Order",
        onClick: handleCreateConsolidatedPOOrder,
        disabled: isDisabled,
        title: isDisabled
          ? "Select transfer orders from the same origin warehouse eligible for consolidation."
          : "",
        isLoading: ordLineForCnsldPOLoading,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDisabled, ordLineForCnsldPOLoading, selectedOrderId],
  );

  return (
    <>
      <li className={`tableButton activeState `}>
        <button
          onClick={importTF}
          disabled={importLoading}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {importLoading && <ButtonSpinner aria-hidden="true" />}
          {importLoading ? "Syncing…" : "Sync"}
        </button>
      </li>
      <li className={`tableButton ${confirmDraftOk ? "activeState" : ""}`}>
        <button
          disabled={!confirmDraftOk || isConfirmingDraft}
          title={
            confirmDraftOk
              ? ""
              : "Confirm Draft is only available when every selected transfer order is Draft."
          }
          onClick={handleConfirmDraft}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isConfirmingDraft && <ButtonSpinner aria-hidden="true" />}
          {isConfirmingDraft ? "Confirming…" : "Confirm Draft"}
        </button>
      </li>
      <ListActionBurgerMenu items={burgerItems} />
    </>
  );
};

export const TransferOrderActionWithCheck = componentWithCondition(TransferOrderAction);
